import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Brain, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';

const TriviaQuiz = ({ user }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuestions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trivia/weekly`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuestions(data);
      }
    } catch (error) {
      toast.error('Failed to load questions');
    }
    setLoading(false);
  };

  const handleAnswer = (questionId, answer) => {
    setSelectedAnswers({ ...selectedAnswers, [questionId]: answer });
  };

  const handleSubmit = async () => {
    if (Object.keys(selectedAnswers).length < questions.length) {
      toast.error('Please answer all questions');
      return;
    }

    setLoading(true);
    try {
      const answers = Object.entries(selectedAnswers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trivia/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ answers })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setSubmitted(true);
        if (data.correct_count >= 3) {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }
        toast.success(`You scored ${data.correct_count}/${data.total_questions}! +${data.points_earned} points`);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit answers');
      }
    } catch (error) {
      toast.error('Connection error');
    }
    setLoading(false);
  };

  const { colors } = useTheme();

  if (loading && !questions.length) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-2xl font-bold" style={{ color: colors.primary, fontFamily: 'var(--font-heading)' }}>Loading trivia...</div>
      </div>
    );
  }

  if (submitted && result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.background }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg"
        >
          <Card className="rounded-3xl p-8 text-center" style={{ backgroundColor: colors.card, boxShadow: 'var(--shadow-lg)' }}>
            <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: colors.primary }}>
              <Brain className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)', color: colors.text }}>Quiz Complete!</h2>
            <p className="text-6xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)', color: colors.primary }}>
              {result.correct_count}/{result.total_questions}
            </p>
            <p className="text-lg mb-6" style={{ color: colors.text_muted }}>Correct Answers</p>
            <div className="rounded-2xl p-6 mb-8" style={{ backgroundColor: `${colors.accent}20` }}>
              <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)', color: colors.accent }}>
                +{result.points_earned} Points
              </p>
            </div>
            <Button
              data-testid="back-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              className="w-full h-14 rounded-full font-bold"
              style={{ backgroundColor: colors.primary, color: colors.text_on_primary }}
            >
              Back to Dashboard
            </Button>
          </Card>
        </motion.div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div style={{ backgroundColor: colors.surface }} className="shadow-sm">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Brain className="w-6 h-6 text-primary" />
            <h1 className="font-heading text-xl font-bold">Weekly Trivia</h1>
          </div>
          <div className="text-sm font-semibold text-muted-foreground">
            {currentIndex + 1}/{questions.length}
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-card p-8"
        >
          <h2 className="font-heading text-2xl font-bold mb-6">{currentQuestion.question}</h2>
          <div className="space-y-3">
            {currentQuestion.options.map((option) => (
              <motion.button
                key={option}
                data-testid={`option-${option}`}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleAnswer(currentQuestion.id, option)}
                className={`w-full p-4 rounded-2xl border-2 text-left font-semibold transition-all ${
                  selectedAnswers[currentQuestion.id] === option
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 hover:border-primary/50 bg-white'
                }`}
              >
                {option}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Navigation */}
        <div className="flex gap-4">
          {currentIndex > 0 && (
            <Button
              data-testid="prev-button"
              onClick={() => setCurrentIndex(currentIndex - 1)}
              variant="outline"
              className="flex-1 h-12 rounded-full font-semibold"
            >
              Previous
            </Button>
          )}
          {currentIndex < questions.length - 1 ? (
            <Button
              data-testid="next-button"
              onClick={() => setCurrentIndex(currentIndex + 1)}
              disabled={!selectedAnswers[currentQuestion.id]}
              className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold"
            >
              Next Question
            </Button>
          ) : (
            <Button
              data-testid="submit-trivia-button"
              onClick={handleSubmit}
              disabled={loading || Object.keys(selectedAnswers).length < questions.length}
              className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-bold shadow-button"
            >
              {loading ? 'Submitting...' : 'Submit Answers'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TriviaQuiz;
